import classNames from 'classnames';
import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import queryString from 'query-string';
import React from 'react';
import { FiEdit, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { routes } from '../constants';
import { Icon } from '../containers/components';
import { contentIcons } from './content-type';
import cpdPointStatus from './cpd-point-status';
import { dateFormats } from './date';
import exportCpdCertificatePdf from '../reports/export-cpd-certificate-pdf';
import { TooltipWrapper } from '../containers/pages/app/partials/tooltip-wrapper';

export const pendingCpdActivityMap = {
  userName: {
    title: 'Who'
  },
  cpdCategory: {
    className: () => 'area-advice',
    transform: ({ cpdCategory }) => cpdCategory
  },
  activityContent: {
    title: 'Title',
    transform: ({ activityContent }) => he.decode(activityContent)
  },
  dateCompleted: {
    transform: ({ dateCompleted }) => dateFnsCompat.format(dateFnsCompat.parse(dateCompleted), dateFormats.ShortDate)
  },
  externalCpdId: {
    title: '',
    className: () => 'edit-row',
    transform: ({ externalCpdId, userId }) => <Link to={`${routes.app.externalCpd.detailsReview(externalCpdId, userId)}`}>Details</Link>
  }
}

export const notSortableColumns = ['Cpd Certificate', 'Edit'];

export const cpdActivityMap = (rejectText, enableExternalCpdOperations = true, ecpdOptions = { review: false }) => ({
  type: {
    transform: ({ type, externalCpdId }) => externalCpdId ?
      <Icon icon={contentIcons.ExternalCpd} /> :
      <Icon icon={fp.get(type)(contentIcons)} />
  },
  cpdCategory: {
    title: 'CPD Category',
    transform: (row) => {
      const { cpdCategory, cpdPointAllocation, isTasaCpd, cpdPoints } = row;

      if (!cpdCategory || cpdCategory === 'None') {
        return 'None';
      }

      const isMultiple = React.isValidElement(cpdCategory) || (Array.isArray(cpdPointAllocation) && cpdPointAllocation.length > 1);
      const cellLabel = isMultiple ? 'Multiple' : (Array.isArray(cpdPointAllocation) && cpdPointAllocation.length === 1 ? cpdPointAllocation[0].name : cpdCategory);

      if (!isMultiple) {
        return cellLabel;
      }

      const fullCategoryArray = Array.isArray(cpdPointAllocation) && cpdPointAllocation.length > 0
        ? cpdPointAllocation
        : [
            {
              cpdCategoryId: 'fallback-id',
              name: cellLabel,
              points: cpdPoints,
              categoryType: 'Other'
          }
        ];

      return (
        <a className='multiple-cpd-trigger'>
          <TooltipWrapper
            category={cellLabel}
            fullCpdCategory={fullCategoryArray}
            className={isTasaCpd ? 'fixed-cat' : 'cpd-category-table-label'}
            trigger={['focus', 'click']}
          />
        </a>
      );
    }
  },
  provider: undefined,
  activityContent: {
    title: 'Content title',
    transform: ({ activityContent, contentId, isContentArchived, showContentLink }) => contentId && !isContentArchived && showContentLink ?
      <Link to={{ pathname: routes.app.professionalProfile.content, search: `?${queryString.stringify({ contentId: contentId })}` }} target='_blank'>
        {he.decode(activityContent)}
      </Link> : he.decode(activityContent)
  },
  cpdPoints: {
    title: 'Total CPD hours',
    transform: ({ cpdPoints, status }) => status === cpdPointStatus.approved ?
      cpdPoints.toFixed(2) :
      status === cpdPointStatus.pending ?
        `${cpdPoints} (pending)` :
        'N/A',
    className: ({ status }) => classNames('cpd-points', {
      denied: status === cpdPointStatus.denied
    })
  },
  status: {
    title: 'Status',
    className: () => 'no-wrap',
    transform: ({ status }) => status === cpdPointStatus.approved ?
      'Passed' :
      status === cpdPointStatus.pending ?
        <span className='text-warning'>Pending</span> :
        status === cpdPointStatus.draft ?
          'Draft' :
          status === cpdPointStatus.registered ?
            'Registered' :
            'Failed'
  },
  dateCompleted: {
    transform: ({ dateCompleted }) => dateFnsCompat.format(dateFnsCompat.parse(dateCompleted), dateFormats.ShortDate)
  },
  downloadCertificate: {
    title: 'Cpd Certificate',
    className: () => 'download-certificate-row',
    transform: ({ externalCpdId, contentBusinessId, status, userName, activityContent, provider, providerLogo, cpdPointAllocation, cpdPoints, dateCompleted, contentId}) => (
      // Certificate is only available for approved caddie content (provider is caddie, not business or external content).
      externalCpdId === undefined && contentBusinessId === undefined && status === cpdPointStatus.approved ?
        <a>
          <FiDownload
            onClick={() => exportCpdCertificatePdf({ userName, activityContent, provider, providerLogo, cpdPointAllocation, cpdPoints, dateCompleted, contentId })}
          />
        </a>
        : null
    )
  },
  externalCpdId: {
    title: 'Edit',
    className: () => 'edit-row',
    transform: ({ externalCpdId, status }) => (
      enableExternalCpdOperations && externalCpdId ?
        ecpdOptions.review && (status === cpdPointStatus.pending || status === cpdPointStatus.approved || status === cpdPointStatus.registered) ?
          <Link to={routes.app.externalCpd.detailsReview(externalCpdId, ecpdOptions.userId)}>Details</Link> :
          !ecpdOptions.review && (status === cpdPointStatus.draft || status === cpdPointStatus.approved || status === cpdPointStatus.registered || status === cpdPointStatus.pending) ?
            <Link to={routes.app.externalCpd.detailsEdit(externalCpdId)}><FiEdit /></Link> : null
        : null
    )
  },
});

export const exportCpdActivityMap = {
  ...cpdActivityMap(),
  cpdCategory: {
    title: 'CPD Category',
    transform: ({ cpdCategory }) => React.isValidElement(cpdCategory) ? 'Multiple' : cpdCategory
  },
  type: {
    ...cpdActivityMap().type,
    transform: undefined
  },
  status: {
    ...cpdActivityMap().status,
    transform: ({ status }) => status === cpdPointStatus.approved ?
      'Passed' :
      status
  },
  externalCpdId: {
    title: 'Activity Source',
    transform: ({ externalCpdId, contentBusinessId }) => externalCpdId ? 'External CPD' : contentBusinessId ? 'Business Content' : 'Caddie Content'
  },
  modeOfLearning: {
    title: 'Mode of Learning',
    transform: ({ externalCpd }) => externalCpd ? fp.startCase(externalCpd.modeOfLearning) : 'Online'
  },
  typeOfCpd: {
    title: 'Type of CPD',
    transform: ({ externalCpd }) => fp.startCase(fp.getOr('Other CPD')('typeOfCpd')(externalCpd))
  },
  cpdPoints: {
    title: 'Total CPD hours',
    transform: ({ cpdPoints, status }) => status === cpdPointStatus.approved ?
      cpdPoints.toFixed(2) :
      status === cpdPointStatus.pending || status === cpdPointStatus.registered ?
        `${cpdPoints} (pending)` :
        'N/A',
    className: ({ status }) => classNames('cpd-points', {
      denied: status === cpdPointStatus.denied
    })
  }
};

export const getCpdCategoryLabel = (
  cpdAllocation,
  cpdCategorys = [],
  documentedCpdPoints
) => {
  const safeAllocation = Array.isArray(cpdAllocation) ? cpdAllocation : [];
  const safeDocumented = Array.isArray(documentedCpdPoints) ? documentedCpdPoints : [];

  const combinedCategories = [
    ...safeAllocation.map(({ cpdCategoryId, points }) => {
      const categoryObj = fp.find(({ id }) => id === cpdCategoryId)(cpdCategorys) || {};
      return {
        cpdCategoryId,
        name: categoryObj.name || 'Unknown Category',
        points: points > 0 ? points : null,
        categoryType: categoryObj.categoryType || 'Other'
      };
    }),
    ...safeDocumented.map(({ name, points }) => ({
      cpdCategoryId: `doc-${name}`,
      name: name || 'Custom CPD',
      points: points > 0 ? points : null,
      categoryType: 'Other'
    }))
  ];

  if (combinedCategories.length === 0) {
    return 'None';
  }

  const fullCpdCategory = fp.sortBy('name')(combinedCategories);
  const isMultiple = fullCpdCategory.length > 1;
  const categoryLabel = isMultiple ? 'Multiple' : fullCpdCategory[0].name;

  return (
    <TooltipWrapper
      fullCpdCategory={fullCpdCategory}
      category={categoryLabel}
      className={isMultiple ? 'multiple-cpd-trigger' : undefined}
      trigger={['focus', 'click']}
    />
  );
};

const attachCategoryInfo = (cpdAllocation, cpdCategorys) => fp.flow(
  fp.map(
    ({ cpdCategoryId, points }) => {
      const aoaWithFullName = fp.find(
        ({ id }) => id === cpdCategoryId
      )(cpdCategorys)
      const name = fp.get('name')(aoaWithFullName);
      const categoryType = fp.get('categoryType')(aoaWithFullName);
      return {
        cpdCategoryId,
        name,
        points,
        categoryType,
      }
    }
  ),
  fp.sortBy(({ name }) => name),
)(cpdAllocation)

/**
 * Map the cpd points got from '/api/v1/cpd-points' endpoint to client side data structure.
 * @returns 
 */
export const mapCpdPoint = (cpdCategorys, publishers) => ({
  title,
  contentType,
  contentId,
  userId,
  userName,
  externalCpdId,
  externalCpd,
  totalPoints,
  provider,
  status,
  timeEarned,
  isTasaCpd,
  contentBusinessId,
  isContentArchived,
  cpdPointAllocation,
  documentedCpdPoints
}) => ({
  id: (contentId && `content-${contentId}`) || (externalCpdId && `external-cpd-${externalCpdId}`),
  status,
  type: contentType,
  cpdCategory: getCpdCategoryLabel(cpdPointAllocation, cpdCategorys, documentedCpdPoints),
  provider: provider.name,
  providerLogo: provider.logo,
  contentId: contentId,
  isContentArchived: isContentArchived,
  activityContent: title,
  cpdPoints: totalPoints,
  dateCompleted: timeEarned,
  externalCpdId: externalCpdId,
  externalCpd,
  userId: userId,
  userName: userName,
  isTasaCpd: isTasaCpd,
  contentBusinessId: contentBusinessId,
  cpdPointAllocation: attachCategoryInfo(cpdPointAllocation,cpdCategorys),
});

export const getCpdActivityField = (field, defaultValue) =>
  (key) => fp.getOr(defaultValue(key))(`${key}.${field}`);

export const getCpdActivityTitle = getCpdActivityField('title', fp.flow(fp.lowerCase, fp.upperFirst));
export const getCpdActivityClassName = getCpdActivityField('className', fp.wrap(fp.kebabCase));
export const getCpdActivityTransform = getCpdActivityField('transform', fp.get);


// WEBPACK FOOTER //
// ./src/constants/cpd-activity.js